// A component fault should cost the operator one screen, not the whole app.
// Without this, a single render error leaves a blank device mid-shift with no
// way back — which is exactly what happened during development.

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/Button';
import { PodMark } from '@/components/ui/Icon';

interface Props {
  children: ReactNode;
  /** Shown instead of the default screen; receives a reset callback. */
  fallback?: (reset: () => void, error: Error) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Keep it in the console for whoever is debugging on the floor.
    console.error('[shopfloor] render error', error, info.componentStack);
  }

  private reset = () => this.setState({ error: null });

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.reset, error);

    return (
      <div
        style={{
          minHeight: '100dvh',
          display: 'grid',
          placeItems: 'center',
          background: 'var(--bg)',
          color: 'var(--ink)',
          padding: 24,
          textAlign: 'center',
        }}
      >
        <div style={{ maxWidth: 320 }}>
          <PodMark size={36} style={{ color: 'var(--sub)', marginBottom: 14 }} />
          <h1 style={{ font: "700 17px 'Space Grotesk',sans-serif", margin: '0 0 8px' }}>
            Something went wrong on this screen
          </h1>
          <p style={{ font: '500 12.5px Manrope,sans-serif', color: 'var(--sub)', margin: '0 0 18px' }}>
            Your work is recorded in Odoo — nothing here is lost. Go back and try again.
          </p>
          <div style={{ display: 'grid', gap: 10 }}>
            <Button variant="primary" size="md" onClick={this.reset}>
              Go back
            </Button>
            <Button variant="soft" size="md" onClick={() => window.location.reload()}>
              Reload the app
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
